import styled from "styled-components";
import media from "helpers/media";
import { theme } from "themes/main";

export const Container = styled.div`
  margin: 3rem 0 0 0;
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  ${media.MD`
    padding: 16px;
    margin: 2rem 0 0 0;
  `}

  @media print {
    display: none;
  }
`;

export const Header = styled.div<{ singleLine?: boolean }>`
  display: flex;
  //  justify-content: space-between;
  gap: 24px;
  align-items: ${({ singleLine }) => (singleLine ? "flex-start" : "center")};
  margin-bottom: 24px;
  flex-direction: ${({ singleLine }) => (singleLine ? "column" : "row")};
  position: relative;

  ${media.MD`
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 16px;
  `}
`;

export const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: #333;
`;

export const TopSection = styled.div`
  align-items: center;
  gap: 24px;
`;

export const Legend = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;

  ${media.MD`
    gap: 16px;
    flex-wrap: wrap;
    width: 100%;
  `}
`;

export const Description = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 2.4rem;
  margin-bottom: 2.4rem;
  max-width: 700px;
`;

export const LegendItem = styled.div<{ singleLine?: boolean }>`
  display: flex;
  border: ${({ singleLine }) => (singleLine ? "none" : "1px solid #cfd8dc")};
  align-items: center;
  gap: 8px;

  padding: 0.8rem 1.2rem;
  border-radius: 0.4rem;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  &:hover {
    background-color: #f8f9fa;
  }

  ${media.MD`
    flex:1;
    
  `}
`;

export const StarIcon = styled.div`
  width: 1.6rem;
  height: 1.6rem;
  position: relative;
  cursor: pointer;

  &::before {
    content: "★";
    position: absolute;
    width: 1.6rem;
    height: 1.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    color: ${theme.colors.treePoppy};
    line-height: 1;
  }
`;

export const LegendCircle = styled.div<{
  type: "required" | "recommended" | "starred";
}>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ type }) =>
    // eslint-disable-next-line no-nested-ternary
    type === "required"
      ? "#E91E63"
      : type === "recommended"
      ? "#FF9800"
      : "#FF9800"};
`;

// Filter Dropdown Styles
export const FilterDropdownContainer = styled.div`
  position: relative;
  z-index: 10;
  flex: 1;
  display: flex;
  justify-content: flex-end;

  ${media.MD`
    justify-content: flex-start;
    align-self: stretch;
    width: 100%;
  `}
`;

export const FilterDropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  border: 1px solid #cfd8dc;
  border-radius: 4px;
  background: white;
  align-self: flex-end;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  min-width: 129px;

  &:hover {
    background-color: #f8f9fa;
  }

  svg {
    width: 16px;
    height: 16px;
    stroke: #59626c;
  }

  ${media.MD`
    width: 100%;
  `}
`;

export const FilterDropdownContent = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  width: 263px;
  background: white;
  border: 1px solid #cfd8dc;
  border-radius: 4px;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.08);
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
  z-index: 100;
`;

export const FilterHeader = styled.div`
  padding: 16px 16px 14px;
  border-bottom: 1px solid #cfd8dc;
  background: #f7f9fa;
`;

export const FilterTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: #f7f9fa;
  font-size: 14px;
  color: #333;
  outline: none;

  &::placeholder {
    color: #59626c;
  }
`;

export const FilterList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding: 0 0 12px;
`;

export const FilterItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  min-height: 32px;
  cursor: pointer;

  &:hover {
    background-color: #f8f9fa;
  }
`;

export const Checkbox = styled.input`
  width: 15px;
  height: 15px;
  margin: 0;
  cursor: pointer;
`;

export const SubjectLabel = styled.label`
  font-size: 14px;
  color: #333;
  cursor: pointer;

  flex: 1;
`;

export const SubjectCount = styled.span`
  font-size: 14px;
  color: #59626c;
`;

export const TableContainer = styled.div`
  overflow: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;

  ${media.MD`
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    position: relative;
    max-width: 92vw;
    
    /* Force hardware acceleration for smooth scrolling */
    transform: translateZ(0);
    will-change: scroll-position;
  `}
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;

  ${media.MD`
    min-width: 100vw;
    width: max-content;
  `}
`;

export const HeaderRow = styled.tr`
  background-color: #f8f9fa;
`;

export const HeaderCell = styled.th<{
  isSmall?: boolean;
  mobileShow?: boolean;
}>`
  padding: 16px 8px;
  text-align: center;
  border-right: 1px solid #e0e0e0;
  position: relative;
  width: 3.2rem;
  min-width: 3.2rem;
  max-width: 3.2rem;

  ${({ mobileShow }) =>
    !mobileShow &&
    media.MD`
     display: none;
    `}

  &:first-child {
    width: ${({ isSmall }) => (isSmall ? "auto !important" : "200px")};
    min-width: ${({ isSmall }) => (isSmall ? "auto !important" : "200px")};
    text-align: left;
    padding-left: 16px;

    ${media.MD`
      position: sticky;
      left: 0;
      background-color: #f8f9fa !important;
      z-index: 20;
      box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
      min-width: 150px;
      width: 150px;
      
      /* Ensure sticky works properly */
      top: auto;
      transform: translateZ(0);
    `}
  }
`;

export const RotatedText = styled.div`
  transform: rotate(-90deg);
  white-space: nowrap;
  font-size: 14px;
  font-weight: 400;
  color: #333;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center;
`;

export const CareerRow = styled.tr<{ isEven: boolean }>`
  background-color: ${({ isEven }) => (isEven ? "#f8f9ff" : "white")};

  &:hover {
    background-color: #e3f2fd;
  }

  ${media.MD`
    /* Ensure first cell gets proper background on mobile */
    td:first-child {
      background-color: inherit !important;
    }
    
    &:hover td:first-child {
      background-color: #e3f2fd !important;
    }
  `}
`;

export const CareerNameCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;

  ${media.MD`
    position: sticky;
    left: 0;
    z-index: 15;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
    min-width: 150px;
    width: 150px;
    
    /* Ensure sticky works properly */
    top: auto;
    transform: translateZ(0);
  `}
`;

export const SubjectCell = styled.td<{ mobileShow?: boolean }>`
  padding: 12px 8px;
  text-align: center;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  position: relative;
  width: 50px;

  ${media.MD`
    min-width: 50px;
    white-space: nowrap;
  `}

  ${({ mobileShow }) =>
    !mobileShow &&
    media.MD`
     display: none;
    `}
`;

export const RequirementCircle = styled.div<{
  type: "required" | "recommended";
}>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ type }) =>
    type === "required" ? "#E91E63" : "#FF9800"};
  margin: 0 auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;
